import React, { useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { emptyArray } from 'ui-components/default-props/empty';

import OverflowHandler from './overflow-handler';
import style from './style.module.less';
import { AppliedFilterPropType } from '../../../prop-types/applied-filter';
import { FilterOptionPropType } from '../../../prop-types/filter-option';
import ClearFilters from '../../filter-bar/clear-filters';
import SavedFiltersDropdown from '../saved-filters';

const FilterBarWithOverflow = ({
  children = emptyArray,
  renderOtherFilters,
  allFilterBys = emptyArray,
  requiredIndices = emptyArray,
  saveFilterPath,
  appliedFilters = emptyArray,
  popularFilterBys = emptyArray,
  clearFilters,
}) => {
  const containerRef = useRef();
  const popularFilterBysIds = popularFilterBys.map((filter) => filter.id);
  const nonLockedFilters = appliedFilters?.filter((f) => !f.isLocked);

  // For filters that do not have the property of "allowsMultipleSelection",
  // we want to include them in the overflow even if there is a similar filter type already applied
  // (so you can apply a second one of that type)
  const modifyOverflowChildren = useCallback(
    (visibleChildren) => {
      const overflowChildren = [];
      const visibleChildrenFilterByIds = visibleChildren.map((c) => c?.props.filterById);

      allFilterBys.forEach((filterBy) => {
        const { allowsMultipleSelection = false, id } = filterBy;
        const isVisible = visibleChildrenFilterByIds.includes(id);
        const isApplied = appliedFilters.some((f) => f.filterById === id);

        if (!isVisible) {
          overflowChildren.push(filterBy);
        }

        if (isApplied && !allowsMultipleSelection) {
          overflowChildren.push(filterBy);
        }
      });

      return overflowChildren;
    },
    [allFilterBys, appliedFilters],
  );

  const popularIndices = useMemo(() => {
    return children.reduce((indices, child, index) => {
      if (popularFilterBysIds.includes(child.props.filterById)) {
        indices.push(index);
      }
      return indices;
    }, []);
  }, [children, popularFilterBysIds]);

  return (
    <div ref={containerRef} className={style['overflow-container']}>
      <OverflowHandler
        appliedFilters={appliedFilters}
        containerRef={containerRef}
        hasSavedFilterPath={!!saveFilterPath}
        popularIndices={popularIndices}
        requiredIndices={requiredIndices}
      >
        {({ visibleIndices }) => {
          if (!visibleIndices) {
            return children;
          }

          const visibleChildren = visibleIndices.map((idx) => children[idx]);
          const nonVisibleFilters = modifyOverflowChildren(visibleChildren);
          const OtherFilters = nonVisibleFilters.length ? renderOtherFilters(nonVisibleFilters) : null;

          return [...visibleChildren, OtherFilters];
        }}
      </OverflowHandler>
      <SavedFiltersDropdown key='saved-filters-dropdown' appliedFilters={appliedFilters} path={saveFilterPath} />
      {nonLockedFilters && nonLockedFilters.length > 0 ? <ClearFilters clearFilters={clearFilters} /> : null}
    </div>
  );
};

FilterBarWithOverflow.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node),
  renderOtherFilters: PropTypes.func.isRequired,
  allFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  requiredIndices: PropTypes.arrayOf(PropTypes.number),
  saveFilterPath: PropTypes.string,
  appliedFilters: PropTypes.arrayOf(AppliedFilterPropType),
  popularFilterBys: PropTypes.arrayOf(AppliedFilterPropType),
  clearFilters: PropTypes.func.isRequired,
};

export default FilterBarWithOverflow;
